import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import type { PlatformSchedulesSearch } from '~/features/platform/types/platform-schedules.types';
import { parsePlatformSchedulesSearch } from '~/features/platform/utils/platform-schedules-search';
import { requireSystemSubsidiaryAdmin } from '~/utils/guards';

const PlatformSchedulesPage = lazy(
  () => import('~/features/platform/pages/platform-schedules-page'),
);

const requirePlatformAdmin = requireSystemSubsidiaryAdmin();

// Lily AI platform admins only — super admin AND the active subsidiary is the system one
// (lily-ai), the rule the workflow-service `/v1/admin/schedules` API enforces (403 otherwise).
// Same guard and sidebar rule as Taxonomy Studio (`canAccessNavPath('/schedules')`), no feature
// flag (see docs/platform-schedules.md).
export const Route = createFileRoute('/_authenticated/_tenant/schedules')({
  beforeLoad: () => {
    requirePlatformAdmin();
  },
  validateSearch: (search: Record<string, unknown>): PlatformSchedulesSearch =>
    parsePlatformSchedulesSearch(search),
  component: RouteComponent,
});

/** Binds the page's filter / sort / tab state to the URL, so views are shareable and
 *  back-button safe. Filter changes replace history; tab changes push. */
function RouteComponent() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  return (
    <Suspense fallback={<PageSkeleton />}>
      <PlatformSchedulesPage
        search={search}
        onSearchChange={(next) => {
          void navigate({
            search: parsePlatformSchedulesSearch({ ...next }),
            replace: (next.tab ?? 'schedules') === (search.tab ?? 'schedules'),
          });
        }}
      />
    </Suspense>
  );
}
